// frontend/src/components/BankStatementImporter.js
//
// Two-step wizard that uploads a CSV/Excel bank statement, lets the user
// confirm or override the auto-detected column mapping, then commits the
// import. Backed by /api/bank-import/preview/ and /api/bank-import/commit/.
// Closes QA Trust #4 from the 2026-05-29 bug report.
//
// Usage:
//   <BankStatementImporter
//      authAxios={authAxios}
//      bankAccounts={[{id, account_name, bank_name}]}
//      defaultBankAccountId={selectedBankAccount}
//      onClose={() => setImporterOpen(false)}
//      onImported={({ statement_id, lines_created }) => ...}
//   />
//
// Backend response shapes are documented in legal_ops/views.py near
// BankStatementImportPreviewView / BankStatementImportCommitView.

import React, { useState, useEffect, useMemo } from 'react';
import { previewBankImport, commitBankImport } from '../api';

const TARGETS = [
  { key: 'date',        label: 'Date',        required: true  },
  { key: 'description', label: 'Description', required: false },
  { key: 'reference',   label: 'Reference',   required: false },
  { key: 'debit',       label: 'Debit',       required: false },
  { key: 'credit',      label: 'Credit',      required: false },
  { key: 'amount',      label: 'Amount (signed)', required: false },
  { key: 'balance',     label: 'Balance',     required: false },
];

function scoreBadge(score) {
  if (score >= 0.8) return { color: 'bg-green-100 text-green-800', label: 'High confidence' };
  if (score >= 0.5) return { color: 'bg-amber-100 text-amber-800', label: 'Medium' };
  if (score > 0)    return { color: 'bg-gray-100 text-gray-700',   label: 'Low' };
  return                   { color: 'bg-red-50 text-red-700',     label: 'No match' };
}

export default function BankStatementImporter({
  authAxios,
  bankAccounts = [],
  defaultBankAccountId = '',
  onClose,
  onImported,
}) {
  const [step, setStep]                   = useState(1);
  const [file, setFile]                   = useState(null);
  const [bankAccountId, setBankAccountId] = useState(defaultBankAccountId);
  const [preview, setPreview]             = useState(null);
  const [columnMap, setColumnMap]         = useState({});
  const [templateName, setTemplateName]   = useState('');
  const [periodStart, setPeriodStart]     = useState('');
  const [periodEnd, setPeriodEnd]         = useState('');
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState('');
  const [result, setResult]               = useState(null);

  useEffect(() => { setBankAccountId(defaultBankAccountId); }, [defaultBankAccountId]);

  const handlePreview = async () => {
    setError('');
    if (!file)          return setError('Please choose a CSV or .xlsx file.');
    if (!bankAccountId) return setError('Pick the bank account to import into.');
    setLoading(true);
    try {
      const data = await previewBankImport(authAxios, { file, bankAccountId });
      setPreview(data);
      // Seed the editable column_map from the backend's suggestions.
      const seed = {};
      Object.entries(data.suggested_mapping || {}).forEach(([target, sug]) => {
        if (sug.source_column) seed[target] = sug.source_column;
      });
      setColumnMap(seed);
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not read this file. Try a CSV exported from your bank portal.');
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    setError('');
    if (!columnMap.date) return setError("The 'Date' column is required — pick which column in your file holds the transaction date.");
    setLoading(true);
    try {
      const data = await commitBankImport(authAxios, {
        file, bankAccountId, columnMap, periodStart, periodEnd, templateName,
      });
      setResult(data);
      setStep(3);
      onImported?.(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Import failed. Check the column mapping and try again.');
    } finally {
      setLoading(false);
    }
  };

  const setMapping = (target, sourceColumn) =>
    setColumnMap((m) => ({ ...m, [target]: sourceColumn || undefined }));

  const headerScores = useMemo(() => {
    if (!preview?.suggested_mapping) return {};
    return preview.suggested_mapping;
  }, [preview]);

  // Source columns already used by another target — surfaced so we can warn.
  const usedColumns = useMemo(() => {
    const counts = {};
    Object.values(columnMap).forEach((c) => { if (c) counts[c] = (counts[c] || 0) + 1; });
    return counts;
  }, [columnMap]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">

        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Import Bank Statement</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Upload a CSV or Excel export from your bank — we'll align the columns for you.
            </p>
          </div>
          <button onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  aria-label="Close importer">&times;</button>
        </div>

        {/* Progress dots */}
        <div className="flex items-center gap-2 px-6 pt-4 text-xs">
          {['Upload', 'Confirm mapping', 'Done'].map((label, i) => {
            const n = i + 1;
            const active = step === n;
            const done = step > n;
            return (
              <div key={label} className="flex items-center gap-2">
                <span className={`h-6 w-6 rounded-full flex items-center justify-center font-semibold
                  ${done ? 'bg-green-600 text-white' : active ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {done ? '✓' : n}
                </span>
                <span className={active ? 'text-gray-900 font-medium' : 'text-gray-500'}>{label}</span>
                {n < 3 && <span className="text-gray-300">›</span>}
              </div>
            );
          })}
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Step 1 — Upload */}
        {step === 1 && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bank account</label>
              <select value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value)}
                      className="w-full border rounded px-3 py-2 text-sm">
                <option value="">— Select bank account —</option>
                {bankAccounts.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.bank_name} — {b.account_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statement file</label>
              <input type="file" accept=".csv,.xlsx,.xlsm,text/csv"
                     onChange={(e) => setFile(e.target.files?.[0] || null)}
                     className="w-full border rounded px-3 py-2 text-sm" />
              <p className="text-xs text-gray-500 mt-1">
                CSV or Excel. First row should be the column headers from your bank.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
                Cancel
              </button>
              <button onClick={handlePreview} disabled={loading || !file || !bankAccountId}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                {loading ? 'Reading…' : 'Read file'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — Confirm mapping */}
        {step === 2 && preview && (
          <div className="p-6 space-y-4">
            <div className="text-sm text-gray-700">
              Found <strong>{preview.row_count}</strong> rows and
              <strong> {preview.headers.length}</strong> columns
              {preview.template_id ? (
                <span className="ml-2 text-green-700">
                  · Mapping loaded from a saved template — review and import.
                </span>
              ) : (
                <span className="ml-2 text-gray-600">
                  · We've guessed how your columns map below. Tweak anything that looks wrong.
                </span>
              )}
            </div>

            {/* Mapping grid */}
            <div className="border rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="text-left px-3 py-2 w-1/3">Target field</th>
                    <th className="text-left px-3 py-2">Your column</th>
                    <th className="text-left px-3 py-2 w-32">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {TARGETS.map(({ key, label, required }) => {
                    const suggestion = headerScores[key] || {};
                    const badge = scoreBadge(suggestion.score || 0);
                    const value = columnMap[key] || '';
                    const duplicate = value && usedColumns[value] > 1;
                    return (
                      <tr key={key} className="border-t">
                        <td className="px-3 py-2 align-top">
                          <div className="font-medium text-gray-800">{label}</div>
                          {required && <span className="text-xs text-red-600">required</span>}
                        </td>
                        <td className="px-3 py-2">
                          <select value={value}
                                  onChange={(e) => setMapping(key, e.target.value)}
                                  className="w-full border rounded px-2 py-1.5 text-sm">
                            <option value="">— Not in this file —</option>
                            {preview.headers.map((h) => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                          {duplicate && (
                            <div className="text-xs text-amber-700 mt-1">
                              Same column is mapped to more than one target. The last commit wins.
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${badge.color}`}>
                            {badge.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Preview rows */}
            {preview.preview_rows?.length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-600 mb-1">First {preview.preview_rows.length} rows from your file:</div>
                <div className="border rounded overflow-x-auto">
                  <table className="text-xs">
                    <thead className="bg-gray-50">
                      <tr>{preview.headers.map((h) => <th key={h} className="px-2 py-1 text-left whitespace-nowrap">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {preview.preview_rows.map((row, i) => (
                        <tr key={i} className="border-t">
                          {row.map((cell, j) => <td key={j} className="px-2 py-1 whitespace-nowrap">{cell == null ? '' : String(cell)}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Optional fields */}
            <div className="grid grid-cols-3 gap-3 pt-2 border-t">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Period start (optional)</label>
                <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}
                       className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Period end (optional)</label>
                <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
                       className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Save mapping as…</label>
                <input type="text" placeholder="e.g. FNB Cheque CSV"
                       value={templateName} onChange={(e) => setTemplateName(e.target.value)}
                       className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
            </div>

            <div className="flex justify-between pt-2 border-t">
              <button onClick={() => setStep(1)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
                Back
              </button>
              <button onClick={handleCommit} disabled={loading}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                {loading ? 'Importing…' : `Import ${preview.row_count} rows`}
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Done */}
        {step === 3 && result && (
          <div className="p-6 space-y-4 text-center">
            <div className="text-5xl">✅</div>
            <div className="text-lg font-medium text-gray-800">Statement imported</div>
            <div className="text-sm text-gray-600">
              {result.lines_created} transactions added for period {result.period_start} – {result.period_end}.
              Your mapping was saved — the next import with the same column headers will skip this wizard.
            </div>
            <div className="flex justify-center gap-2 pt-2 border-t">
              <button onClick={onClose} className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
